import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import LoadingBar from 'react-top-loading-bar';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowsRotate,
  faCheck,
  faCircleQuestion,
  faCopy,
  faGear,
  faHourglassHalf,
  faListUl,
  faPenToSquare,
  faStar,
  faWandMagicSparkles,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import { reprocessBump } from '../reprocessBump';
import './valueaddMyBump.css';

// AI outputs for a bumped video. All data comes from viewDoneBump's live listener on the bump doc.
// New uploads get timestamps only; the other cards appear for videos processed before April 2026 that have them.
const ValueAddMyBump = ({ videoId, user, isProcessedbyAI, isTranscriptEmpty, hasTimestamps, summary, titles, takeaways, hashtags, timestamps }) => {
  if (!isProcessedbyAI) {
    return (
      <section className="vab-section">
        <ProcessingCard isTranscriptEmpty={isTranscriptEmpty} />
      </section>
    );
  }

  const timestampLines = typeof timestamps === 'string' ? timestamps.split('\n').filter(Boolean) : timestamps;
  const hasLegacyOutputs = !!summary || takeaways.length > 0 || titles.length > 0 || hashtags.length > 0;

  return (
    <section className="vab-section">
      <div className="vab-section-header">
        <h2 className="vab-section-title">AI outputs</h2>
        <p className="vab-section-subtitle">
          Generated from your video. Copy them anywhere, or add <code>{'{{VIDEO_TIMESTAMPS}}'}</code> to your description.
        </p>
      </div>

      {!hasTimestamps && <ReprocessBanner videoId={videoId} user={user} />}

      <div className="vab-grid">
        <div className="vab-column">
          <OutputCard
            title="Video Timestamps"
            help="Timestamps split your video into sections viewers can jump between, and make your content more discoverable."
            helpLink="/help/articles/9377422-how-do-video-timestamps-help-you"
            copyText={timestampLines.join('\n')}
            actions={
              <Link to="/settings/creator" className="vab-icon-btn" title="Branding" aria-label="Branding settings">
                <FontAwesomeIcon icon={faGear} />
              </Link>
            }
          >
            {timestampLines.length > 0 ? (
              <div className="vab-ts-list">
                {timestampLines.map((line, i) => {
                  const [time, ...rest] = line.split(' - ');
                  return (
                    <div key={i} className="vab-ts-row">
                      <span className="vab-ts-time">{time}</span>
                      <span className="vab-ts-text">{rest.join(' - ')}</span>
                    </div>
                  );
                })}
              </div>
            ) : <EmptyOutput />}
          </OutputCard>

          {hashtags.length > 0 && (
            <OutputCard
              title="Video Tags"
              help="Hashtags that associate your video with topics, trends and communities your audience follows. Starred tags are the strongest."
              helpLink="/help/articles/9381190-how-do-video-tags-help-you"
              copyText={hashtags.join(', ')}
            >
              <div className="vab-tags">
                {hashtags.map((tag, i) => (
                  <span key={i} className={`vab-tag${i < 5 ? ' is-best' : ''}`}>
                    {i < 5 && <FontAwesomeIcon icon={faStar} className="vab-tag-star" />}
                    {tag}
                  </span>
                ))}
              </div>
            </OutputCard>
          )}
        </div>

        <div className="vab-column">
          {summary && (
            <OutputCard
              title="Video Description"
              help="An optimized description that helps viewers find your video through search, with keywords at the start of the overview."
              helpLink="/help/articles/9381182-how-does-a-video-description-help-you"
              copyText={summary}
            >
              <p className="vab-summary">{summary}</p>
            </OutputCard>
          )}

          {takeaways.length > 0 && (
            <OutputCard
              title="Key Takeaways"
              help="Key takeaways add relevant keywords to your description, increasing visibility in Google, Bing and other search engines."
              helpLink="/help/articles/9381198-how-do-key-takeaways-help-you"
              copyText={takeaways.join('\n')}
            >
              <ul className="vab-list">
                {takeaways.map((takeaway, i) => {
                  const { emoji, text } = splitEmoji(takeaway);
                  return (
                    <li key={i} className="vab-list-item">
                      {emoji && <span className="vab-takeaway-emoji">{emoji}</span>}
                      <span>{text}</span>
                    </li>
                  );
                })}
              </ul>
            </OutputCard>
          )}

          {titles.length > 0 && (
            <OutputCard
              title="Video Titles"
              help="Title options optimized for your video's visibility and engagement."
              helpLink="/help/articles/9542805-how-do-optimized-video-titles-help-you"
              copyText={titles.join('\n')}
            >
              <ul className="vab-list">
                {titles.map((title, i) => <li key={i} className="vab-list-item">{title}</li>)}
              </ul>
            </OutputCard>
          )}

          <div className="vab-card">
            <div className="vab-card-header">
              <h3 className="vab-card-title">Guides</h3>
            </div>
            <div className="vab-guides">
              {GUIDES.map(g => (
                <a key={g.link} href={g.link} target="_blank" rel="noopener noreferrer" className="vab-guide">
                  <span className="vab-guide-icon"><FontAwesomeIcon icon={g.icon} /></span>
                  <span className="vab-guide-text">
                    <span className="vab-guide-title">{g.title}</span>
                    <span className="vab-guide-description">{g.description}</span>
                  </span>
                </a>
              ))}
            </div>
          </div>
          {hasLegacyOutputs && (
            <p className="vab-legacy-note">
              The other outputs here came from an earlier version of Bump AI. New uploads get timestamps, and you can
              ask for the rest in a <Link to="/workspace">workspace</Link> chat.
            </p>
          )}
        </div>
      </div>
    </section>
  );
};

const GUIDES = [
  {
    title: 'Bump AI variables',
    description: 'Add {{VIDEO_TIMESTAMPS}} to put your chapters in the description.',
    link: '/help/articles/9742235-what-are-the-bump-ai-variables',
    icon: faPenToSquare,
  },
  {
    title: 'What timestamps do',
    description: 'Sections improve viewer navigation and boost SEO.',
    link: '/help/articles/9377422-how-do-video-timestamps-help-you',
    icon: faListUl,
  },
  {
    title: 'Your processing time',
    description: 'Minutes are deducted per video, rounded in your favor.',
    link: '/help/articles/8858577-how-are-minutes-deducted',
    icon: faHourglassHalf,
  },
];

const splitEmoji = (takeaway) => {
  const match = takeaway.match(/(\p{Emoji_Presentation}|\p{Extended_Pictographic}|\p{Emoji_Modifier_Base}|\p{Emoji_Component})+/gu);
  if (!match) return { emoji: '', text: takeaway };
  return { emoji: match[0], text: takeaway.replace(match[0], '').trim() };
};

const EmptyOutput = () => (
  <p className="vab-empty">No timestamps yet. Reprocess the video for free to generate them.</p>
);

const OutputCard = ({ title, help, helpLink, copyText, actions, children }) => {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 3000);
    return () => clearTimeout(t);
  }, [copied]);

  const copy = () => {
    navigator.clipboard.writeText(copyText || '');
    setCopied(true);
  };

  return (
    <div className="vab-card">
      <div className="vab-card-header">
        <div className="vab-card-heading">
          <h3 className="vab-card-title">{title}</h3>
          <HelpTip text={help} link={helpLink} />
        </div>
        <div className="vab-card-actions">
          {actions}
          <button className="vab-icon-btn" onClick={copy} disabled={!copyText} title={copied ? 'Copied' : 'Copy'} aria-label={`Copy ${title}`}>
            <FontAwesomeIcon icon={copied ? faCheck : faCopy} className={copied ? 'is-copied' : ''} />
          </button>
        </div>
      </div>
      {children}
    </div>
  );
};

const HelpTip = ({ text, link }) => (
  <span className="vab-help" tabIndex={0}>
    <FontAwesomeIcon icon={faCircleQuestion} className="vab-help-icon" />
    <span className="vab-help-tip" role="tooltip">
      {text} <a href={link}>Learn more</a>
    </span>
  </span>
);

const ReprocessBanner = ({ videoId, user }) => {
  const debounceKey = `${user.uid}-${videoId}-debounce`;
  const readDebounce = () => {
    const until = parseInt(sessionStorage.getItem(debounceKey), 10);
    return until && Date.now() < until ? until : 0;
  };
  const [debouncedUntil, setDebouncedUntil] = useState(readDebounce);
  const [isRunning, setIsRunning] = useState(false);
  const [progress, setProgress] = useState(0);

  // Re-enable the button once the 60 s debounce runs out
  useEffect(() => {
    if (!debouncedUntil) return;
    const t = setTimeout(() => setDebouncedUntil(0), Math.max(debouncedUntil - Date.now(), 0));
    return () => clearTimeout(t);
  }, [debouncedUntil]);

  // Creep the top loading bar while the reprocess call is in flight
  useEffect(() => {
    if (!isRunning) return;
    setProgress(10);
    const iv = setInterval(() => setProgress(p => Math.min(p + 5, 90)), 1500);
    return () => clearInterval(iv);
  }, [isRunning]);

  const handleReprocess = async () => {
    const until = Date.now() + 60000;
    sessionStorage.setItem(debounceKey, String(until));
    setDebouncedUntil(until);
    setIsRunning(true);
    await reprocessBump(videoId); // swallows its own errors; outputs arrive via the bump doc listener
    setIsRunning(false);
    setProgress(100);
  };

  return (
    <div className="vab-reprocess">
      <LoadingBar color="#4add80" progress={progress} onLoaderFinished={() => setProgress(0)} />
      <FontAwesomeIcon icon={faTriangleExclamation} className="vab-reprocess-icon" />
      <p className="vab-reprocess-text">
        Timestamps are missing. Reprocess this video for <strong>free</strong> to generate them.{' '}
        <a href="/help/articles/9416266-does-reprocessing-my-video-deduct-minutes">Learn more</a>
      </p>
      <button className="vab-reprocess-btn" onClick={handleReprocess} disabled={isRunning || !!debouncedUntil}>
        <FontAwesomeIcon icon={faArrowsRotate} spin={isRunning} />
        {isRunning ? 'Reprocessing…' : 'Reprocess'}
      </button>
    </div>
  );
};

const ProcessingCard = ({ isTranscriptEmpty }) => (
  <div className={`vab-processing${isTranscriptEmpty ? ' is-failed' : ''}`}>
    <span className="vab-processing-icon">
      <FontAwesomeIcon icon={isTranscriptEmpty ? faTriangleExclamation : faWandMagicSparkles} />
    </span>
    <div className="vab-processing-text">
      <h3 className="vab-processing-title">{isTranscriptEmpty ? 'This video could not be processed' : 'Bump AI is watching your video'}</h3>
      <p className="vab-processing-description">
        {isTranscriptEmpty
          ? 'Videos with limited or no spoken words, like music videos, may not meet our requirements.'
          : 'It is generating your timestamps. They will appear here automatically, usually within a few minutes.'}
      </p>
    </div>
    <a
      className="vab-processing-link"
      href={isTranscriptEmpty ? '/help/articles/8835886-what-are-the-video-requirements' : '/help/articles/9742280-how-does-bump-ai-process-my-video'}
      target="_blank"
      rel="noopener noreferrer"
    >
      {isTranscriptEmpty ? 'Video requirements' : 'How it works'}
    </a>
  </div>
);

export default ValueAddMyBump;
